import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { API_BASE_URL } from '@/lib/api-client';

/**
 * Sprint 41 W35-cutover (frontend) — redirect bridge from legacy
 * customer URLs (`/proposal/<legacyProposalId>`) to the new
 * snapshot-backed flow (`/p/<publicToken>`).
 *
 * Replaces the deleted `ClientProposalPage`. The W35-A backend
 * migration seeded one `LegacyProposalRedirect` row per non-deleted
 * `ProjectProposal`, so any URL that resolved against the legacy
 * page should resolve here too. Unknown / never-seeded IDs return
 * the same 404 shape as the new `/p/:token` 404 — generic copy, no
 * existence leak.
 *
 * Lifecycle on a single visit:
 *   1. Read `proposalId` from the URL params.
 *   2. Hit `GET /proposal-redirect/:legacyId` (public, no auth).
 *   3. Backend returns `{ publicToken }` — minting a snapshot on
 *      first call, returning the cached one thereafter.
 *   4. `navigate('/p/<publicToken>', { replace: true })` swaps the
 *      URL in-place so the browser back button doesn't bounce the
 *      customer through the redirect again.
 *
 * The loading card is intentionally brief — most redirects resolve
 * in one round-trip from the cached path. The mint path runs the
 * full resolver, which can take 1-3s on large projects; the spinner
 * covers that window.
 */
async function fetchRedirect(
  legacyProposalId: string,
): Promise<{ publicToken: string }> {
  const response = await fetch(
    `${API_BASE_URL}/proposal-redirect/${legacyProposalId}`,
  );
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as {
      message?: string;
    };
    throw new Error(err.message || 'Proposal not found.');
  }
  return response.json() as Promise<{ publicToken: string }>;
}

export default function LegacyProposalRedirect() {
  const { proposalId } = useParams<{ proposalId: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery<
    { publicToken: string },
    Error
  >({
    queryKey: ['legacy-proposal-redirect', proposalId],
    queryFn: () => fetchRedirect(proposalId!),
    enabled: Boolean(proposalId),
    // The redirect target is stable once minted — cache for the
    // session.
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (data?.publicToken) {
      navigate(`/p/${data.publicToken}`, { replace: true });
    }
  }, [data, navigate]);

  if (isError) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="font-newsreader text-xl font-semibold tracking-tight text-foreground mb-2">
              Proposal not available
            </h2>
            <p className="text-sm text-muted-foreground">
              {error?.message ||
                'This proposal link may have expired or been revoked. Please contact your sales rep for a fresh link.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-muted-foreground">
          {isLoading ? 'Loading proposal…' : 'Redirecting…'}
        </p>
      </div>
    </div>
  );
}
